import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Detects valid Supabase auth hashes (recovery / invite / magic link / signup)
 * and redirects to /admin/reset-password preserving the hash, so users invited
 * via email land on the password creation page instead of the homepage.
 *
 * Safeguards against redirect loops:
 * - Only redirects when BOTH a valid `type` AND an `access_token` are present.
 * - Never redirects if already on /admin/reset-password.
 * - Ignores auth error hashes (e.g. expired link) so the user sees the error page.
 */
const VALID_TYPES = new Set(["recovery", "invite", "magiclink", "signup"]);

const AuthHashRedirect = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // Already on the target page — never redirect (prevents loops)
    if (pathname === "/admin/reset-password") return;

    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    const params = new URLSearchParams(hash.startsWith("#") ? hash.substring(1) : hash);

    // If Supabase returned an error in the hash, do not hijack it
    if (params.get("error") || params.get("error_code")) return;

    const type = params.get("type");
    const accessToken = params.get("access_token");

    // Require BOTH a valid auth type AND an access_token to redirect
    if (!type || !VALID_TYPES.has(type)) return;
    if (!accessToken) return;

    navigate(`/admin/reset-password${hash}`, { replace: true });
  }, [pathname, navigate]);

  return null;
};

export default AuthHashRedirect;
